"use client"

import { X, MapPin, Search } from "lucide-react"
import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import AddressAutocomplete from "./address-autocomplete"
import { leadSourcesAPI } from "../services/api"

const CustomerModal = ({ isOpen, onClose, onSave, customer, isEditing = false }) => {
  const navigate = useNavigate()
  const modalRef = useRef(null)
  const [customerData, setCustomerData] = useState({
    fullName: "",
    address: "",
    suite: "",
    phone: "",
    email: "",
    notes: "",
    city: "",
    state: "",
    zipCode: "",
    source: ""
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [validationErrors, setValidationErrors] = useState({})
  const [isValidatingEmail, setIsValidatingEmail] = useState(false)
  const [isValidatingPhone, setIsValidatingPhone] = useState(false)
  const [leadSources, setLeadSources] = useState([])
  const [showCustomSource, setShowCustomSource] = useState(false)
  const [customSource, setCustomSource] = useState("")

  // Refs for autofill detection
  const addressRef = useRef(null)
  const emailRef = useRef(null)
  const phoneRef = useRef(null)

  // API base URL - using backend proxy for Google Places API to avoid CORS issues
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://service-flow-backend-production-4568.up.railway.app/api'

  // Function to parse combined address into street address and suite
  const parseCombinedAddress = (combinedAddress) => {
    if (!combinedAddress) return { address: "", suite: "" }
    
    // Common patterns for suite indicators
    const suitePatterns = [
      /,\s*(apt|apartment|unit|suite|ste|#|no|number)\s*\.?\s*([^,]+)/i,
      /,\s*([^,]+)\s*(apt|apartment|unit|suite|ste|#|no|number)\s*\.?\s*([^,]+)/i,
      /,\s*([^,]+)$/ // Last part after comma
    ]
    
    // Try to match suite patterns
    for (const pattern of suitePatterns) {
      const match = combinedAddress.match(pattern)
      if (match) {
        // Extract the suite part and the rest as address
        const suitePart = match[0].replace(/^,\s*/, '') // Remove leading comma and space
        const addressPart = combinedAddress.replace(match[0], '').trim()
        return {
          address: addressPart,
          suite: suitePart
        }
      }
    }
    
    // If no pattern matches, check if there's a comma (common separator)
    const commaIndex = combinedAddress.lastIndexOf(',')
    if (commaIndex > 0) {
      const addressPart = combinedAddress.substring(0, commaIndex).trim()
      const suitePart = combinedAddress.substring(commaIndex + 1).trim()
      
      // Only split if the part after comma looks like a suite
      if (suitePart && suitePart.length < 20) { // Reasonable length for suite
        return {
          address: addressPart,
          suite: suitePart
        }
      }
    }
    
    // If no clear separation, return the whole thing as address
    return {
      address: combinedAddress,
      suite: ""
    }
  }

  useEffect(() => {
    console.log('CustomerModal useEffect:', { isOpen, isEditing, customer })
    if (!isOpen) {
      // Clear form data when modal closes
      setCustomerData({
        fullName: "",
        address: "",
        suite: "",
        phone: "",
        email: "",
        notes: "",
        city: "",
        state: "",
        zipCode: "",
        source: ""
      })
      setError("")
      setValidationErrors({})
      setIsValidatingEmail(false)
      setIsValidatingPhone(false)
    } else if (isOpen && !isEditing) {
      // Clear form data when opening for new customer (not editing)
      setCustomerData({
        fullName: "",
        address: "",
        suite: "",
        phone: "",
        email: "",
        notes: "",
        city: "",
        state: "",
        zipCode: "",
        source: ""
      })
      setError("")
      setValidationErrors({})
      setIsValidatingEmail(false)
      setIsValidatingPhone(false)
    } else if (isEditing && customer) {
      // Populate form with existing customer data for editing
      console.log('Populating form with customer data:', customer)

      // Parse the combined address if it exists (for backward compatibility)
      const parsedAddress = parseCombinedAddress(customer.address)

      const joinedName = [customer.first_name, customer.last_name].filter(Boolean).join(' ').trim()
      setCustomerData({
        fullName: joinedName,
        address: customer.suite ? customer.address : parsedAddress.address,
        suite: customer.suite || parsedAddress.suite,
        phone: customer.phone || "",
        email: customer.email || "",
        notes: customer.notes || "",
        city: customer.city || "",
        state: customer.state || "",
        zipCode: customer.zip_code || "",
        source: customer.source || ""
      })
    }
  }, [isOpen, isEditing, customer])

  // Chrome autofill often populates `type="tel"` / `type="email"` inputs without
  // firing React's synthetic change event, so the DOM value diverges from state.
  // We listen for `animationstart` — the standard webkit-autofill signal — and
  // ONLY sync the field that fired. Each write is gated by a plausibility check
  // so a half-filled or wrong-category autofill (e.g. a saved short code landing
  // in the phone field) never silently overwrites what the user actually sees.
  useEffect(() => {
    if (!isOpen) return

    const isPlausible = (key, value) => {
      if (!value) return false
      if (key === 'phone') return value.replace(/\D/g, '').length >= 10
      if (key === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
      if (key === 'address') return value.trim().length >= 4
      return true
    }

    const syncOne = (key, ref) => {
      const value = ref?.value || ''
      if (!isPlausible(key, value)) return
      setCustomerData(prev => (prev[key] === value ? prev : { ...prev, [key]: value }))
    }

    const inputs = [
      { ref: emailRef.current, key: 'email' },
      { ref: phoneRef.current, key: 'phone' }
      // addressRef points to the AddressAutocomplete wrapper (not an <input>);
      // its onChange flow already updates state, so we skip it here.
    ].filter(x => x.ref)

    const handlers = inputs.map(({ ref, key }) => {
      const onAnimationStart = (e) => {
        if (e?.animationName && /autofill/i.test(e.animationName)) {
          syncOne(key, ref)
        }
      }
      ref.addEventListener('animationstart', onAnimationStart)
      return { ref, onAnimationStart }
    })

    return () => {
      handlers.forEach(({ ref, onAnimationStart }) => {
        ref.removeEventListener('animationstart', onAnimationStart)
      })
    }
  }, [isOpen])

  // Add click-outside handler to close modal (excluding Google Places autocomplete)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        // Don't close if clicking on Google Places autocomplete suggestions
        if (event.target.closest('.pac-container') || 
            event.target.closest('[data-autocomplete-suggestions]') ||
            event.target.closest('.autocomplete-suggestions') ||
            event.target.closest('[role="listbox"]') ||
            event.target.closest('.pac-item')) {
          return
        }
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  // Load lead sources when modal opens
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    ;(async () => {
      try {
        const data = await leadSourcesAPI.list()
        let list = (data?.sources || []).map(s => s.name)
        if (!list.length) {
          try {
            const seeded = await leadSourcesAPI.seed()
            list = (seeded?.sources || []).map(s => s.name)
          } catch {}
        }
        if (!cancelled) setLeadSources(list)
      } catch {
        if (!cancelled) {
          setLeadSources(['Website', 'Referral', 'Cold Call', 'Social Media', 'Google', 'Thumbtack', 'Yelp', 'Facebook', 'Other'])
        }
      }
    })()
    return () => { cancelled = true }
  }, [isOpen])

  // Reset custom-source UI when modal closes/opens
  useEffect(() => {
    if (!isOpen) {
      setShowCustomSource(false)
      setCustomSource("")
    }
  }, [isOpen])

  const addCustomSource = (name) => {
    const trimmed = (name || '').trim()
    if (!trimmed) return
    if (!leadSources.includes(trimmed)) {
      setLeadSources(prev => [...prev, trimmed])
      leadSourcesAPI.create(trimmed).catch(() => {})
    }
    setCustomerData(prev => ({ ...prev, source: trimmed }))
    setCustomSource("")
    setShowCustomSource(false)
  }

  const validateEmail = async (email) => {
    if (!email) return true // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const isValidFormat = emailRegex.test(email)
    
    if (!isValidFormat) return false
    
    // Additional validation: check for common disposable email domains
    const disposableDomains = [
      '10minutemail.com', 'tempmail.org', 'guerrillamail.com', 'mailinator.com',
      'yopmail.com', 'throwaway.email', 'temp-mail.org', 'fakeinbox.com'
    ]
    
    const domain = email.split('@')[1]?.toLowerCase()
    if (disposableDomains.includes(domain)) {
      return false
    }
    
    return true
  }

  const validatePhone = (phone) => {
    if (!phone) return true // Phone is optional
    // Remove all formatting characters (spaces, dashes, parentheses)
    const cleaned = phone.replace(/[\s\-()]/g, '')
    // More flexible validation: allow common phone number formats
    const phoneRegex = /^[\+]?[1-9][\d]{6,15}$/
    return phoneRegex.test(cleaned)
  }

  const validateField = async (field, value) => {
    const errors = { ...validationErrors }
    
    switch (field) {
      case 'email':
        if (value) {
          setIsValidatingEmail(true)
          const isValid = await validateEmail(value)
          if (!isValid) {
            errors.email = 'Please enter a valid email address'
          } else {
            delete errors.email
          }
          setIsValidatingEmail(false)
        } else {
          delete errors.email
        }
        break
      case 'phone':
        if (value) {
          setIsValidatingPhone(true)
          const isValid = validatePhone(value)
          if (!isValid) {
            errors.phone = 'Please enter a valid phone number (must start with a digit 1-9)'
          } else {
            delete errors.phone
          }
          setIsValidatingPhone(false)
        } else {
          delete errors.phone
        }
        break
      case 'fullName':
        if (!value.trim()) {
          errors.fullName = 'Name is required'
        } else {
          delete errors.fullName
        }
        break
      default:
        // No validation needed for other fields
        break
    }
    
    setValidationErrors(errors)
  }

  const formatPhone = (phone) => {
    if (!phone) return phone
    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '')
    
    // Format based on length
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`
    } else if (cleaned.length === 11 && cleaned[0] === '1') {
      return `+1 (${cleaned.slice(1,4)}) ${cleaned.slice(4,7)}-${cleaned.slice(7)}`
    } else if (cleaned.length > 10) {
      // International format
      return `+${cleaned}`
    }
    return phone
  }

  const handlePhoneChange = async (e) => {
    const value = e.target.value
    const formatted = formatPhone(value)
    setCustomerData({ ...customerData, phone: formatted })
    // Validate phone in real-time as user types
    if (formatted) {
      await validateField('phone', formatted)
    } else {
      // Clear phone validation error if field is empty
      setValidationErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors.phone
        return newErrors
      })
    }
  }

  const handleEmailChange = async (e) => {
    const value = e.target.value
    setCustomerData({ ...customerData, email: value })
    // Validate email in real-time as user types
    if (value) {
      await validateField('email', value)
    } else {
      // Clear email validation error if field is empty
      setValidationErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors.email
        return newErrors
      })
    }
  }

  const splitFullName = (fullName) => {
    const trimmed = (fullName || '').trim().replace(/\s+/g, ' ')
    if (!trimmed) return { firstName: '', lastName: '' }
    const parts = trimmed.split(' ')
    if (parts.length === 1) return { firstName: parts[0], lastName: '' }
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') }
  }

  const handleAddressSelect = (addressData) => {
    console.log('🏠 Address selected from autocomplete:', addressData)
    setCustomerData({
      ...customerData,
      address: addressData.formattedAddress,
      city: addressData.components.city,
      state: addressData.components.state,
      zipCode: addressData.components.zipCode
    })
  }

  const handleAddressChange = (value) => {
    console.log('✏️ Address manually changed:', value)
    setCustomerData(prev => ({ 
      ...prev, 
      address: value,
      // Clear city, state, zipCode when address is manually changed
      city: "",
      state: "",
      zipCode: ""
    }))
  }


  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Clear previous errors
    setError("")
    
    // Validate required fields
    if (!customerData.fullName.trim()) {
      setError('Name is required')
      return
    }
    
    // Validate email if provided
    if (customerData.email && !(await validateEmail(customerData.email))) {
      setError('Please enter a valid email address')
      return
    }
    
    // Validate phone if provided
    if (customerData.phone && !validatePhone(customerData.phone)) {
      setError('Please enter a valid phone number (must start with a digit 1-9)')
      return
    }
    
    setLoading(true)
    
    try {
      // Format phone number for server (remove all formatting, keep only digits and +)
      const formattedPhone = customerData.phone ? customerData.phone.replace(/[\s\-()]/g, '') : ''

      const { firstName, lastName } = splitFullName(customerData.fullName)
      const customerToSave = {
        firstName,
        lastName,
        address: customerData.address,
        suite: customerData.suite,
        phone: formattedPhone,
        email: customerData.email,
        notes: customerData.notes,
        city: customerData.city,
        state: customerData.state,
        zipCode: customerData.zipCode,
        source: customerData.source ? customerData.source.trim() : ""
      }
      
      console.log('Submitting customer data:', customerToSave)
      const result = await onSave(customerToSave)
      console.log('Customer saved successfully:', result)
      
      // Close modal
      onClose()
      // Do NOT navigate away. The parent will select the new customer.
    } catch (error) {
      console.error('Error in customer modal submit:', error)
      const serverMessage = error?.response?.data?.error || error?.response?.data?.message
      setError(serverMessage || error.message || 'Failed to save customer. Please try again.')
      // Don't close the modal on error - keep it open so user can fix the issue
    } finally {
      setLoading(false)
    }
  }

  console.log('CustomerModal render:', { isOpen, isEditing, customer })
  if (!isOpen) return null

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-start sm:items-center justify-center z-50 p-4 overflow-y-auto"
    >
      <div 
        ref={modalRef}
        className="bg-white rounded-xl w-full max-w-md relative my-4 sm:my-6 max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)] overflow-hidden flex flex-col"
      >
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-6 border-b border-[var(--sf-border-light)]">
          <h2 className="text-xl font-semibold text-[var(--sf-text-primary)]">{isEditing ? 'Edit Customer' : 'New Customer'}</h2>
          <button
            onClick={(e) => {
              e.preventDefault()
              onClose()
            }}
            className="text-[var(--sf-text-muted)] hover:text-[var(--sf-text-muted)] hover:bg-[var(--sf-bg-hover)] p-1 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <form id="customer-form" onSubmit={handleSubmit} className="space-y-6" noValidate>
            {/* Customer Name */}
            <div>
              <label className="block text-sm font-medium text-[var(--sf-text-primary)] mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Full name"
                value={customerData.fullName}
                onChange={(e) => {
                  setCustomerData({ ...customerData, fullName: e.target.value })
                  validateField('fullName', e.target.value)
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)] focus:border-[var(--sf-blue-500)] text-[var(--sf-text-primary)] text-sm ${
                  validationErrors.fullName ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-[var(--sf-border-light)]'
                }`}
                required
              />
              {validationErrors.fullName && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.fullName}</p>
              )}
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-[var(--sf-text-primary)] mb-1">
                Address
              </label>
              <AddressAutocomplete
                ref={addressRef}
                value={customerData.address}
                onChange={handleAddressChange}
                onAddressSelect={handleAddressSelect}
                placeholder="Street Address"
                className="w-full"
                showValidationResults={true}
              />
            </div>

            {/* Apartment/Unit */}
            <div>
              <label className="block text-sm font-medium text-[var(--sf-text-primary)] mb-1">
                Apartment/Unit
              </label>
              <input
                type="text"
                placeholder="Apt, Unit, Suite, Floor, etc."
                value={customerData.suite}
                onChange={(e) => setCustomerData({ ...customerData, suite: e.target.value })}
                className="w-full px-3 py-2 border border-[var(--sf-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)] focus:border-[var(--sf-blue-500)] text-[var(--sf-text-primary)] text-sm"
              />
            </div>

            {/* City, State, Zip Code */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--sf-text-primary)] mb-1">
                  City
                </label>
                <input
                  type="text"
                  placeholder="City"
                  value={customerData.city}
                  onChange={(e) => setCustomerData({ ...customerData, city: e.target.value })}
                  className="w-full px-3 py-2 border border-[var(--sf-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)] focus:border-[var(--sf-blue-500)] text-[var(--sf-text-primary)] text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--sf-text-primary)] mb-1">
                  State
                </label>
                <input
                  type="text"
                  placeholder="State"
                  value={customerData.state}
                  onChange={(e) => setCustomerData({ ...customerData, state: e.target.value })}
                  className="w-full px-3 py-2 border border-[var(--sf-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)] focus:border-[var(--sf-blue-500)] text-[var(--sf-text-primary)] text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--sf-text-primary)] mb-1">
                  Zip Code
                </label>
                <input
                  type="text"
                  placeholder="12345"
                  value={customerData.zipCode}
                  onChange={(e) => setCustomerData({ ...customerData, zipCode: e.target.value })}
                  className="w-full px-3 py-2 border border-[var(--sf-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)] focus:border-[var(--sf-blue-500)] text-[var(--sf-text-primary)] text-sm"
                />
              </div>
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-sm font-medium text-[var(--sf-text-primary)] mb-1">
                Phone Number
              </label>
              <div className="relative">
                <input
                  ref={phoneRef}
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={customerData.phone}
                  onChange={handlePhoneChange}
                  maxLength={20}
                  autoComplete="tel"
                  list="phone-suggestions"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)] focus:border-[var(--sf-blue-500)] text-[var(--sf-text-primary)] text-sm ${
                    validationErrors.phone ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-[var(--sf-border-light)]'
                  }`}
                />
                <datalist id="phone-suggestions">
                  <option value="(555) 123-4567" />
                  <option value="(555) 987-6543" />
                  <option value="+1 (555) 123-4567" />
                  <option value="555-123-4567" />
                </datalist>
                {isValidatingPhone && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--sf-blue-500)]"></div>
                  </div>
                )}
              </div>
              {validationErrors.phone && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.phone}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-[var(--sf-text-primary)] mb-1">
                Email
              </label>
              <div className="relative">
                <input
                  ref={emailRef}
                  type="email"
                  placeholder="email@example.com"
                  value={customerData.email}
                  onChange={handleEmailChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)] focus:border-[var(--sf-blue-500)] text-[var(--sf-text-primary)] text-sm ${
                    validationErrors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-[var(--sf-border-light)]'
                  }`}
                />
                {isValidatingEmail && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--sf-blue-500)]"></div>
                  </div>
                )}
              </div>
              {validationErrors.email && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
              )}
              {!customerData.email && (
                <div className="mt-2 p-3 bg-[var(--sf-blue-50)] border border-[var(--sf-border-light)] rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-[var(--sf-blue-500)]" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-[var(--sf-blue-500)]">
                        <strong>Recommended:</strong> Add an email address to automatically send job confirmations and updates to your customer.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Source (e.g. Website, Referral – preserved when converting from lead) */}
            <div>
              <label className="block text-sm font-medium text-[var(--sf-text-primary)] mb-1">
                Source
              </label>
              <select
                value={
                  showCustomSource
                    ? '__custom__'
                    : (customerData.source && (leadSources.includes(customerData.source) || customerData.source === '')
                        ? customerData.source
                        : (customerData.source ? customerData.source : ''))
                }
                onChange={(e) => {
                  const value = e.target.value
                  if (value === '__custom__') {
                    setShowCustomSource(true)
                    setCustomSource('')
                  } else {
                    setShowCustomSource(false)
                    setCustomSource('')
                    setCustomerData({ ...customerData, source: value })
                  }
                }}
                className="w-full px-3 py-2 border border-[var(--sf-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)] focus:border-[var(--sf-blue-500)] text-[var(--sf-text-primary)] text-sm bg-white"
              >
                <option value="">Select a source...</option>
                {leadSources.map((source) => (
                  <option key={source} value={source}>{source}</option>
                ))}
                {customerData.source && !leadSources.includes(customerData.source) && !showCustomSource && (
                  <option value={customerData.source}>{customerData.source}</option>
                )}
                <option value="__custom__">+ Add custom source</option>
              </select>

              {showCustomSource && (
                <div className="mt-2 p-3 bg-[var(--sf-bg-page)] border border-[var(--sf-border-light)] rounded-lg">
                  <label className="block text-xs font-medium text-[var(--sf-text-primary)] mb-1">
                    Custom source name
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={customSource}
                      onChange={(e) => setCustomSource(e.target.value)}
                      placeholder="Enter custom source name"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && customSource.trim()) {
                          e.preventDefault()
                          addCustomSource(customSource)
                        } else if (e.key === 'Escape') {
                          setCustomSource('')
                          setShowCustomSource(false)
                        }
                      }}
                      className="flex-1 px-3 py-2 border border-[var(--sf-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)] focus:border-[var(--sf-blue-500)] text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => addCustomSource(customSource)}
                      disabled={!customSource.trim()}
                      className="px-3 py-2 bg-[var(--sf-blue-500)] text-white rounded-lg hover:bg-[var(--sf-blue-600)] text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCustomSource('')
                        setShowCustomSource(false)
                      }}
                      className="px-3 py-2 bg-white border border-[var(--sf-border-light)] text-[var(--sf-text-secondary)] rounded-lg hover:bg-[var(--sf-bg-hover)] text-sm font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-[var(--sf-text-primary)] mb-1">
                Notes
              </label>
              <textarea
                placeholder="Customer notes..."
                value={customerData.notes}
                onChange={(e) => setCustomerData({ ...customerData, notes: e.target.value })}
                className="w-full px-3 py-2 border border-[var(--sf-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)] focus:border-[var(--sf-blue-500)] text-[var(--sf-text-primary)] text-sm"
                rows="3"
              />
            </div>

          </form>
        </div>

        {/* Footer - Fixed */}
        <div className="flex justify-end space-x-3 p-6 border-t border-[var(--sf-border-light)] bg-[var(--sf-bg-page)]">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              onClose()
            }}
            className="px-4 py-2 text-sm font-semibold text-[var(--sf-text-secondary)] bg-white border border-[var(--sf-border-light)] rounded-lg hover:bg-[var(--sf-bg-hover)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--sf-blue-500)]"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="customer-form"
            disabled={loading || Object.keys(validationErrors).length > 0}
            className="px-4 py-2 text-sm font-semibold text-white bg-[var(--sf-blue-500)] rounded-lg hover:bg-[var(--sf-blue-600)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--sf-blue-500)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Saving..." : "Save Customer"}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CustomerModal 